if (!customElements.get('trumoto-intro-film')) {
  customElements.define('trumoto-intro-film', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.video = this.querySelector('video');
      this.audio = this.querySelector('[data-intro-audio]');
      this.replay = this.querySelector('[data-intro-replay]');
      this.stage = this.closest('.tm-story__hero, [data-story-slide]');
      if (!this.video || !this.audio || !this.replay || !this.stage) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.controls = new window.TruMotoVideoControls(this.video, { ...options, host: this, native: false });
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.video.muted = this.video.defaultMuted;
      this.video.loop = false;
      this.video.addEventListener('playing', () => this.begin(), options);
      this.video.addEventListener('loadeddata', () => {
        if (this.dialog?.open) this.playFirstVisit();
      }, options);
      this.video.addEventListener('ended', () => this.finish(), options);
      this.video.addEventListener('volumechange', () => this.updateAudio(), options);
      this.video.addEventListener('error', () => this.finish(), options);
      this.audio.addEventListener('click', () => {
        this.video.muted = !this.video.muted;
        this.updateAudio();
        // Mobile playback may be suspended when autoplay or sound is blocked.
        // Request audible playback directly in the tap, while it is authorized.
        if (this.dialog?.open || (!this.video.muted && !this.video.ended)) this.playFirstVisit();
      }, options);
      this.replay.addEventListener('click', () => {
        this.begin();
        this.video.currentTime = 0;
        this.video.play().catch(() => this.finish());
      }, options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) { this.video.pause(); this.finish(); }
      }, options);
      this.updateAudio();
      let firstVisit = false;
      if (this.hasAttribute('data-first-visit')) {
        try {
          firstVisit = localStorage.getItem('trumoto-galaxy-intro-seen-v1') !== 'true';
          localStorage.setItem('trumoto-galaxy-intro-seen-v1', 'true');
        } catch { firstVisit = true; }
      }
      const autoplayEnabled = this.closest('trumoto-product-story')?.dataset.video !== 'false';
      if (!autoplayEnabled || this.motion.matches || this.video.ended || (this.video.hasAttribute('data-intro-blocked') && !firstVisit)) this.finish();
      else {
        this.begin();
        if (firstVisit) this.showFirstVisit(options);
      }
    }

    begin() {
      this.stage.dataset.introState = 'playing';
      this.video.removeAttribute('data-intro-complete');
      this.video.removeAttribute('data-intro-blocked');
      this.replay.hidden = true;
      if (this.start) this.start.hidden = true;
    }

    finish() {
      this.stage.dataset.introState = 'complete';
      this.video.setAttribute('data-intro-complete', '');
      this.replay.hidden = false;
      this.closeFirstVisit();
    }

    showFirstVisit(options) {
      this.dialog = document.createElement('dialog');
      this.dialog.className = 'tm-intro-dialog';
      this.dialog.setAttribute('aria-label', this.video.getAttribute('aria-label'));
      this.screen = document.createElement('div');
      this.screen.className = 'tm-intro-dialog__screen';
      this.screen.tabIndex = -1;
      this.screen.setAttribute('autofocus', '');
      this.screen.append(this.video, this.audio, this.replay);
      this.skip = document.createElement('button');
      this.skip.type = 'button';
      this.skip.className = 'tm-intro-button tm-intro-skip';
      this.skip.textContent = this.dataset.skipLabel;
      this.start = document.createElement('button');
      this.start.type = 'button';
      this.start.className = 'tm-intro-button tm-intro-start';
      this.start.textContent = this.dataset.playLabel;
      this.start.hidden = true;
      this.screen.append(this.skip, this.start);
      this.dialog.append(this.screen);
      this.append(this.dialog);
      this.previousOverflow = { html: document.documentElement.style.overflow, body: document.body.style.overflow };
      try { this.dialog.showModal(); }
      catch { this.closeFirstVisit(); return; }
      this.screen.focus({ preventScroll: true });
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      this.video.muted = false;
      this.updateAudio();
      this.skip.addEventListener('click', () => { this.video.pause(); this.finish(); }, options);
      this.start.addEventListener('click', () => this.playFirstVisit(), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.video.pause(); this.finish(); }, options);
      const source = this.video.querySelector('source');
      const portrait = matchMedia('(max-width: 749px) and (orientation: portrait)').matches;
      const src = portrait ? this.video.dataset.portraitSrc : this.video.dataset.landscapeSrc;
      if (source && src && source.getAttribute('src') !== src) {
        source.src = src;
        source.removeAttribute('data-src');
        this.video.poster = portrait ? this.video.dataset.portraitPoster : this.video.dataset.landscapePoster;
        this.video.load();
      }
      if (this.video.readyState >= 2) this.playFirstVisit();
    }

    playFirstVisit() {
      this.video.play().then(() => {
        if (this.dialog?.open) this.start.hidden = true;
      }).catch(() => this.playFailed());
    }

    playFailed() {
      if (this.dialog?.open) this.start.hidden = false;
      else this.finish();
    }

    closeFirstVisit() {
      if (!this.dialog) return;
      this.video.pause();
      if (this.dialog.open) this.dialog.close();
      this.prepend(this.video);
      this.append(this.audio, this.replay);
      this.dialog.remove();
      this.dialog = null;
      document.documentElement.style.overflow = this.previousOverflow.html;
      document.body.style.overflow = this.previousOverflow.body;
      this.video.muted = true;
      this.updateAudio();
    }

    updateAudio() {
      const label = this.video.muted ? this.dataset.unmuteLabel : this.dataset.muteLabel;
      this.audio.querySelector('span').textContent = label;
      this.audio.setAttribute('aria-label', label);
      this.audio.setAttribute('aria-pressed', String(!this.video.muted));
      this.toggleAttribute('data-muted', this.video.muted);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.video?.pause();
      this.closeFirstVisit();
      this.controller = null;
    }
  });
}
